import { useMemo } from "react"
import { useCompanyConnectorControllerGetCompanyConnectorsQuery } from "../api/companyConnectorApi"
import { useConnectorControllerGetAllConnectorsQuery } from "../api/connectorApi"
import { CONNECTORS_TO_SOURCES_MAP, SOURCES_MAP } from "../constants/sources"
import { BRAND_COLORS_MAP } from "../utils/chart-utils"
import { ReleaseCycle } from "../api/types"

export type TConnector = {
  id: string
  isConnected: boolean
  isBeta: boolean
}

export type TSource = {
  id: string
  name: string
  icon: string
  color: string
  isBeta: boolean
}

export type TSourceMap = Map<string, TSource>

export default function useSources() {
  const {
    data: connectedServices,
    isLoading: isServicesLoading,
    isSuccess: isServicesSuccess,
    refetch,
  } = useCompanyConnectorControllerGetCompanyConnectorsQuery()
  const {
    data: connectorsLib,
    isLoading: isConnectorsLoading,
    isSuccess: isServicesConnectors,
  } = useConnectorControllerGetAllConnectorsQuery()

  // map existing company connector status to the connector sources
  const connectedConnectors: TConnector[] = useMemo(
    () =>
      (connectorsLib ?? [])
        ?.map(({ id }) => ({
          id,
          isBeta:
            connectorsLib?.find(connector => connector.id === id)
              ?.releaseCycle === ReleaseCycle.BETA,
          isConnected:
            connectedServices?.some(
              connector =>
                connector.serviceName === id && connector.isCompleted,
            ) ?? false,
        }))
        .filter(({ isConnected }) => !!isConnected),
    [connectorsLib, connectedServices],
  )

  type SourceWithBeta = { sourceId: string; isBeta: boolean }
  const connectedSourceIds: SourceWithBeta[] = useMemo(
    () =>
      connectedConnectors.reduce<SourceWithBeta[]>(
        (acc, { id, isBeta }: TConnector) => {
          const sourceConnectors = CONNECTORS_TO_SOURCES_MAP[id] || []
          return [
            ...acc,
            ...sourceConnectors.map(sourceId => ({ sourceId, isBeta })),
          ]
        },
        [],
      ),
    [connectedConnectors],
  )

  const sources: TSource[] = useMemo(
    () =>
      [...new Set(connectedSourceIds.map(item => item.sourceId))].map(
        sourceId => ({
          ...SOURCES_MAP[sourceId],
          color: BRAND_COLORS_MAP[sourceId],
          isBeta:
            connectedSourceIds.find(item => item.sourceId === sourceId)
              ?.isBeta || false,
        }),
      ),
    [connectedSourceIds],
  )

  const sourcesMap: TSourceMap = useMemo(
    () =>
      new Map(
        sources.map(({ id, name, icon, color, isBeta }) => [
          id,
          { name, icon, id, color, isBeta },
        ]),
      ),
    [sources],
  )

  return {
    sources: sourcesMap,
    refetch,
    isLoading:
      isServicesLoading ||
      isConnectorsLoading ||
      // if source query was not successful. we just trick it to be in loading state
      // Otherwise, we will have a flickering effect where components are rendered
      // and resubscribed to the query thus resulting in re-requesting failed request
      // and we end up in an infinite loop
      !(isServicesSuccess && isServicesConnectors),
  }
}
